interface SparklineProps {
  /** Series points in chronological order. */
  data: number[];
  /** Render width in px. */
  width?: number;
  /** Render height in px. */
  height?: number;
  /** CSS color for the line + fill. */
  color?: string;
  /** Optional area fill underneath the line. Uses `color` at low opacity. */
  filled?: boolean;
}

/**
 * Tiny SVG sparkline. No axes, no labels — meant to sit inside dashboard
 * cards as a "shape of the period" visual. Hand-rolled so we don't pull in a
 * charting library just for a 80x24 viz.
 */
export function Sparkline({
  data,
  width = 96,
  height = 28,
  color = "var(--color-accent)",
  filled = true,
}: SparklineProps) {
  if (data.length === 0) return null;
  if (data.length === 1) {
    // Single point — render a horizontal line at half height.
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke={color}
          strokeWidth={1.5}
          strokeLinecap="round"
        />
      </svg>
    );
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);

  const points = data.map((v, i) => {
    const x = i * stepX;
    // Invert Y (SVG origin is top-left) and inset by 1px so the stroke sits
    // inside the viewBox.
    const y = height - 1 - ((v - min) / range) * (height - 2);
    return { x, y };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(" ");

  const areaPath = filled
    ? `${linePath} L${width.toFixed(2)},${height} L0,${height} Z`
    : null;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden
    >
      {areaPath && (
        <path d={areaPath} fill={color} fillOpacity={0.12} stroke="none" />
      )}
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
